import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LOCALES } from '../../i18n'
import { setApiLocale } from '../../lib/api/client'
import { ErrorBoundary } from '../ErrorBoundary'
import { MobileFooterNav } from './MobileFooterNav'
import type { Locale } from '../../lib/api/types'

// Funnel steps render StepShell's own sticky bottom action bar — hide the footer nav there.
const FUNNEL_STEPS = new Set(['book', 'rate', 'info', 'payment', 'summary'])

/** Validates the :locale segment and syncs it to i18n + the API client. */
export function LocaleLayout() {
  const { locale } = useParams()
  const { i18n } = useTranslation()
  const location = useLocation()
  const valid = locale && SUPPORTED_LOCALES.includes(locale as Locale)

  useEffect(() => {
    if (valid) {
      const l = locale as Locale
      if (i18n.language !== l) i18n.changeLanguage(l)
      setApiLocale(l)
      window.localStorage.setItem('gsm_locale', l)
      document.documentElement.lang = l
    }
  }, [locale, valid, i18n])

  if (!valid) return <Navigate to={`/${i18n.language || 'it'}`} replace />

  // Show the mobile footer nav on content pages; hide it on the booking funnel (its StepShell
  // already has a bottom action bar). Add matching bottom padding so content clears the bar.
  const page = location.pathname.split('/').filter(Boolean)[1] ?? ''
  const showFooterNav = !FUNNEL_STEPS.has(page)

  // Per-route guard: a crash on one screen shows the fallback and auto-recovers on navigation.
  return (
    <ErrorBoundary resetKey={location.pathname}>
      {showFooterNav ? (
        <div className="pb-[calc(60px_+_env(safe-area-inset-bottom))] lg:pb-0">
          <Outlet />
        </div>
      ) : (
        <Outlet />
      )}
      {showFooterNav && <MobileFooterNav />}
    </ErrorBoundary>
  )
}
